import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import z from "zod";
import { DatePicker } from "@/components/Planning/components/DatePicker";
import { TIMING_YEAR_START, TIMING_YEAR_END } from "@/utils/dateFormat";
import api from "@/api";
import { getUserInfo } from "@/user";
import { DIARY_MAX_LENGTH } from "@/utils/trialFieldLimits";
import { apiErrorMessage } from "@/utils/apiError";
import CharCount from "@/components/CharCount";
import CompletedBySelect from "@/components/CompletedBySelect";

// Same year range as the timing modes (Exact date/Month position/Week numbers/Seasons) - see
// `dateFormat.js`. Only this Done-date field; nothing else in this file changes.
const DONE_DATE_START_MONTH = new Date(TIMING_YEAR_START, 0, 1);
const DONE_DATE_END_MONTH = new Date(TIMING_YEAR_END, 11, 31);
import { STAGE_ORDER } from "@/components/Planning/constants";
import { toast } from "sonner";
import { isLastTaskForStage } from "@/utils/taskLogic";
import { closingTaskName, isTrialClosed } from "@/utils/trialClose";
import { CloseTrialDialog } from "@/components/CloseTrialDialog";
import { Check } from "lucide-react";

// The stages a completed task can move a trial on to: never to Closed - a trial is only closed by a user
// (utils/trialClose.js)
const ADVANCING_STAGES = STAGE_ORDER.filter((stage) => stage !== "Closed");

export function CompleteTaskDialog({ trial, task, onTaskComplete }) {
  const [open, setOpen] = useState(false);
  // After Final Report or Trial Done: "Close the trial now?", with the trial as it is now (null when not asked).
  // The list is refreshed only after it, as the task's card goes once its task is done.
  const [closePrompt, setClosePrompt] = useState(null);
  // Who did it (customer point 61): chosen, by default the signed-in user, who the API records as who registered it
  const [me, setMe] = useState(null);
  const [completedBy, setCompletedBy] = useState("");
  useEffect(() => {
    if (!open) return;
    getUserInfo().then((user) => {
      setMe(user);
      setCompletedBy(user?.id ?? "");
    });
  }, [open]);

  // BBCH is free text, as on the diary entry (e.g. "31-32", "at seeding"); empty leaves the task's BBCH as it is
  const [BBCH, setBBCH] = useState("");
  const [doneDate, setDoneDate] = useState(new Date());
  const [errors, setErrors] = useState({});

  const CompleteTaskSchema = z.object({
    bbch: z.string().max(DIARY_MAX_LENGTH.bbch, `BBCH must be at most ${DIARY_MAX_LENGTH.bbch} characters`),
    doneDate: z.date({ required_error: "Done date is required" }),
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("Submitting:", { bbch: BBCH, doneDate });

    // Clear previous errors
    setErrors({});

    const result = CompleteTaskSchema.safeParse({
      bbch: BBCH,
      doneDate,
    });

    console.log("Validation result:", result);

    if (!result.success) {
      console.log("Full error object:", result.error);
      // Map Zod errors to error state
      const fieldErrors = {};
      if (result.error && result.error.issues) {
        result.error.issues.forEach((err) => {
          fieldErrors[err.path[0]] = err.message;
        });
      }
      console.log("Field errors:", fieldErrors);
      setErrors(fieldErrors);
      return;
    }

    try {
      // Format doneDate as YYYY-MM-DD (local) to avoid timezone shifts when server stores UTC timestamps
      const formatDateYMD = (d) => {
        if (!(d instanceof Date)) return d;
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        return `${yyyy}-${mm}-${dd}`;
      };

      const donedatePayload = formatDateYMD(doneDate);

      const bbch = BBCH.trim();
      let response = await api.patch(`/trials/${trial.id}/diaries/${task.id}`, {
        ...(bbch ? { bbch } : {}),
        donedate: donedatePayload,
        ...(completedBy ? { completed_by: completedBy } : {}),
      });
      console.log(
        "API response:",
        response,
        "payload donedate:",
        donedatePayload
      );
      console.log("API response:", response);

      // Check if this completed task is the last one for the current stage
      const updatedDiaries = trial.diaries.map((diary) =>
        diary.id === task.id
          ? { ...diary, bbch: bbch || diary.bbch, completed_by: completedBy || me?.id, donedate: donedatePayload }
          : diary
      );

      // Handle stage advancement
      const shouldAdvanceStage = isLastTaskForStage(
        trial,
        task,
        updatedDiaries,
        trial.stage
      );

      // If this was the last task for the stage, advance to next stage
      if (shouldAdvanceStage) {
        await handleNextStage(trial);
      }
      console.log("shouldAdvanceStage:", shouldAdvanceStage);

      // Close dialog and trigger refresh
      setOpen(false);
      if (closingTaskName(task) && !isTrialClosed(trial)) {
        setClosePrompt({ ...trial, diaries: updatedDiaries });
        return;
      }

      // Call the callback to refresh task data
      if (onTaskComplete) {
        onTaskComplete();
      }
    } catch (error) {
      console.error("Error completing task:", error);
      setErrors({ form: apiErrorMessage(error, "Failed to complete the task.") });
    }
  };

  const handleNextStage = async (trial) => {
    const currentStage = trial.stage;
    const currentIndex = ADVANCING_STAGES.findIndex(
      (stage) => stage.toLowerCase() === currentStage?.toLowerCase()
    );

    // Report is the last stage a task moves a trial to
    if (currentIndex === -1 || currentIndex >= ADVANCING_STAGES.length - 1) return;

    const nextStage = ADVANCING_STAGES[currentIndex + 1];
    const toastId = toast.loading(`Moving to ${nextStage}...`);

    try {
      // Only the stage: an old trial may hold values the API no longer accepts in other fields
      await api.post(`/trials/${trial.id}/update`, { stage: nextStage });
      toast.success(`Moved to ${nextStage}`, { id: toastId });
    } catch (error) {
      toast.error("Failed to update stage", { id: toastId });
      console.error("Next stage error:", error);
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen} modal={false}>
      <DialogTrigger asChild>
        <Button className="bg-green-600 hover:bg-green-400" aria-label="Complete task" title="Complete task">
          <Check className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Complete Task</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4">
            <div className="grid gap-3">
              <Label htmlFor="bbch">BBCH</Label>
              <Input
                id="bbch"
                name="bbch"
                value={BBCH}
                onChange={(e) => setBBCH(e.target.value)}
                maxLength={DIARY_MAX_LENGTH.bbch}
                placeholder='e.g. "31-32" or "at seeding"'
              />
              <CharCount value={BBCH} max={DIARY_MAX_LENGTH.bbch} />
              {errors.bbch && (
                <span className="text-red-500 text-xs">{errors.bbch}</span>
              )}
            </div>
            {errors.form && (
              <p role="alert" className="text-sm text-red-600 dark:text-red-400">{errors.form}</p>
            )}
            <div className="grid gap-3" data-testid="completed-by">
              <Label htmlFor="complete-completed-by">Completed by</Label>
              <CompletedBySelect id="complete-completed-by" value={completedBy} onChange={setCompletedBy} />
            </div>
            <div className="grid gap-3">
              <Label htmlFor="doneDate">Done date</Label>
              <DatePicker
                selected={doneDate}
                onChange={(date) => setDoneDate(date)}
                startMonth={DONE_DATE_START_MONTH}
                endMonth={DONE_DATE_END_MONTH}
              />
              {errors.doneDate && (
                <span className="text-red-500 text-xs">{errors.doneDate}</span>
              )}
            </div>
          </div>
          <DialogFooter className="mt-4">
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button className="bg-green-600 hover:bg-green-400" type="submit">
              Save changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
      {/* A dialog of its own (not inside the content above, which is closed by then) */}
      {closePrompt && (
        <CloseTrialDialog
          trial={closePrompt}
          open
          askFor={closingTaskName(task)}
          onOpenChange={(next) => {
            if (next) return;
            setClosePrompt(null);
            if (onTaskComplete) onTaskComplete();
          }}
        />
      )}
    </Dialog>
  );
}
